import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SERVICES = [
  {
    icon: '🌐',
    title: 'Desarrollo Web Full Stack',
    short: 'Apps escalables con React + Node.js',
    desc: 'Desde una landing page hasta una plataforma compleja con auth, pagos, integraciones y panel admin. Stack moderno, código limpio, deploy en la nube.',
    tags: ['React', 'Node.js', 'MongoDB', 'API REST'],
    price: 'Desde $350 USD',
  },
  {
    icon: '🤖',
    title: 'IoT & Automatización',
    short: 'Hardware + Software conectados',
    desc: 'Sistemas embebidos con ESP32/Arduino que se comunican con la nube. Sensores, actuadores, dashboards en tiempo real. El mundo físico digitalizado.',
    tags: ['ESP32', 'MQTT', 'C++', 'Dashboard'],
    price: 'Desde $400 USD',
  },
  {
    icon: '🧠',
    title: 'Integración de IA',
    short: 'Agentes inteligentes para tu negocio',
    desc: 'Chatbots con contexto real, asistentes de presupuesto, análisis automático de datos. No es un bot genérico — es un agente que conoce tu negocio.',
    tags: ['Groq', 'LLaMA', 'RAG', 'Agentes'],
    price: 'Desde $500 USD',
  },
  {
    icon: '☁️',
    title: 'Deploy & DevOps',
    short: 'Tu app en producción, estable y rápida',
    desc: 'Configuración de servidores, CI/CD, Docker, dominios, SSL y monitoreo. De localhost a producción con las mejores prácticas.',
    tags: ['Docker', 'Railway', 'Vercel', 'Nginx'],
    price: 'Desde $200 USD',
  },
  {
    icon: '📱',
    title: 'Aplicaciones Móviles',
    short: 'React Native para iOS & Android',
    desc: 'Apps nativas con una sola base de código. GPS, cámara, notificaciones push, integración con APIs propias.',
    tags: ['React Native', 'Expo', 'iOS', 'Android'],
    price: 'Desde $600 USD',
  },
  {
    icon: '🎓',
    title: 'Docencia & Talleres',
    short: 'Programación y robótica para todos',
    desc: 'Imparto cursos de programación, robótica y automatización en escuelas y empresas. Metodología práctica, resultados reales.',
    tags: ['Robótica', 'Programación', 'IoT', 'Talleres'],
    price: 'A consultar',
  },
];

const ServiceCard = ({ service, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.1 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,69,0,0.06)' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${hovered ? 'rgba(255,69,0,0.4)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '16px', padding: '1.75rem',
        transition: 'all 0.3s', cursor: 'default',
        transform: hovered ? 'translateY(-5px)' : 'none',
        boxShadow: hovered ? '0 8px 30px rgba(255,69,0,0.15)' : 'none',
      }}
    >
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{service.icon}</div>
      <h3 style={{ color: '#f2f2f2', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.3rem' }}>{service.title}</h3>
      <p style={{ color: '#ff7b47', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{service.short}</p>
      <AnimatePresence>
        {hovered && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ color: 'rgba(242,242,242,0.6)', fontSize: '0.83rem', lineHeight: 1.7, marginBottom: '1rem', overflow: 'hidden' }}
          >
            {service.desc}
          </motion.p>
        )}
      </AnimatePresence>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
        {service.tags.map(t => (
          <span key={t} style={{
            padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.72rem',
            background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.2)', color: '#ff7b47',
          }}>{t}</span>
        ))}
      </div>
      <div style={{ color: '#ff4500', fontWeight: 700, fontSize: '0.9rem' }}>{service.price}</div>
    </motion.div>
  );
};

// Akira Project Feature Card
const AkiraFeature = () => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    style={{
      background: 'linear-gradient(135deg, rgba(255,69,0,0.08) 0%, rgba(255,69,0,0.03) 100%)',
      border: '1px solid rgba(255,69,0,0.3)',
      borderRadius: '20px', padding: '2.5rem',
      marginBottom: '4rem', position: 'relative', overflow: 'hidden',
    }}
  >
    {/* Glow */}
    <div style={{
      position: 'absolute', top: '-50%', right: '-10%', width: '400px', height: '400px',
      background: 'radial-gradient(circle, rgba(255,69,0,0.08) 0%, transparent 70%)',
      pointerEvents: 'none',
    }} />

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', alignItems: 'center' }}>
      <div>
        <div style={{
          display: 'inline-block', padding: '0.3rem 0.8rem', borderRadius: '20px',
          background: 'rgba(255,69,0,0.15)', border: '1px solid rgba(255,69,0,0.3)',
          color: '#ff4500', fontSize: '0.72rem', letterSpacing: '2px',
          textTransform: 'uppercase', marginBottom: '1rem',
        }}>
          🏍️ Proyecto Estrella — En desarrollo
        </div>
        <h2 style={{ color: '#f2f2f2', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: '1rem', lineHeight: 1.2 }}>
          Proyecto <span style={{ color: '#ff4500' }}>Akira</span>
        </h2>
        <p style={{ color: 'rgba(242,242,242,0.65)', lineHeight: 1.8, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          Una alarma inteligente para motos que combina hardware embebido (ESP32), conectividad IoT,
          y una aplicación web/móvil en la nube. Sensores de vibración, GPS, notificaciones en tiempo real
          y control remoto desde el celular.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {['ESP32', 'MQTT', 'Node.js', 'React Native', 'GPS', 'Sensores', 'Notificaciones push', 'MongoDB'].map(t => (
            <span key={t} style={{
              padding: '0.3rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem',
              background: 'rgba(255,69,0,0.1)', border: '1px solid rgba(255,69,0,0.25)', color: '#ff7b47',
            }}>{t}</span>
          ))}
        </div>
      </div>
      <div>
        {[
          { icon: '📡', title: 'Conectividad en tiempo real', desc: 'MQTT sobre WiFi/4G para comunicación instantánea entre la moto y el servidor.' },
          { icon: '🔔', title: 'Alertas inteligentes', desc: 'Detección de vibración anormal, movimiento sin autorización e intrusión.' },
          { icon: '📱', title: 'Control desde el celular', desc: 'App móvil para activar/desactivar, ver historial y recibir notificaciones push.' },
          { icon: '🗺️', title: 'GPS & Geofencing', desc: 'Tracking en tiempo real y alertas si la moto sale de una zona definida.' },
        ].map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}
          >
            <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>{f.icon}</div>
            <div>
              <h4 style={{ color: '#f2f2f2', fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.2rem' }}>{f.title}</h4>
              <p style={{ color: 'rgba(242,242,242,0.5)', fontSize: '0.8rem', lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

export default function ServicesSection() {

  return (
    <section id="landingpage" style={{ background: '#0a0a0a', padding: '6rem 0' }}>
      <style>{`
        .cta-hero-btn:hover { transform:translateY(-2px); box-shadow:0 0 30px rgba(255,69,0,0.5) !important; }
      `}</style>

      {/* Hero services banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255,69,0,0.12) 0%, rgba(15,15,15,0) 60%)',
        borderBottom: '1px solid rgba(255,69,0,0.1)',
        padding: '5rem 1.5rem', textAlign: 'center', marginBottom: '5rem',
      }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '1rem' }}>Lo que construyo</p>
          <h1 style={{ fontSize: 'clamp(2rem, 6vw, 3.5rem)', color: '#f2f2f2', fontWeight: 800, lineHeight: 1.2, marginBottom: '1.25rem' }}>
            Servicios de Desarrollo<br />
            <span style={{ color: '#ff4500' }}>& Automatización con IA</span>
          </h1>
          <p style={{ color: 'rgba(242,242,242,0.5)', maxWidth: '600px', margin: '0 auto 2rem', lineHeight: 1.8 }}>
            Llevo tu idea de la hoja a producción. Desde apps web hasta sistemas IoT físicos,
            con inteligencia artificial integrada cuando tiene sentido.
          </p>
          <motion.a
            href="#contact"
            className="cta-hero-btn"
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-block', padding: '0.9rem 2.5rem',
              background: 'linear-gradient(135deg, #ff4500, #ff7b47)',
              color: 'white', borderRadius: '8px', textDecoration: 'none',
              fontWeight: 700, fontSize: '1rem', transition: 'all 0.3s',
              boxShadow: '0 0 20px rgba(255,69,0,0.3)',
            }}
          >
            Solicitar presupuesto →
          </motion.a>
        </motion.div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Akira Feature */}
        <AkiraFeature />

        {/* Services grid */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Mis servicios</p>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', color: '#f2f2f2', fontWeight: 800 }}>¿Qué puedo hacer por vos?</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem', marginBottom: '4rem' }}>
          {SERVICES.map((s, i) => <ServiceCard key={s.title} service={s} index={i} />)}
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            textAlign: 'center', background: 'rgba(255,69,0,0.05)',
            border: '1px solid rgba(255,69,0,0.2)', borderRadius: '16px',
            padding: '3rem 2rem',
          }}
        >
          <h2 style={{ color: '#f2f2f2', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, marginBottom: '0.75rem' }}>
            ¿Tenés una idea en mente?
          </h2>
          <p style={{ color: 'rgba(242,242,242,0.5)', marginBottom: '1.5rem' }}>
            Hablemos. El 🤖 de abajo a la derecha puede darte una estimación ahora mismo.
          </p>
          <a href="#contact" style={{
            display: 'inline-block', padding: '0.8rem 2rem',
            background: 'linear-gradient(135deg, #ff4500, #ff7b47)',
            color: 'white', borderRadius: '8px', textDecoration: 'none',
            fontWeight: 700, fontSize: '0.95rem',
          }}>
            Contactar ahora
          </a>
        </motion.div>
      </div>
    </section>
  );
}
